import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ShieldCheck, Truck, FlaskConical, FileCheck2, CalendarClock,
  CreditCard, ArrowRight, PackageCheck, ClipboardList, Banknote, MessageCircle, Gift,
} from 'lucide-react';
import ProductCard from '../components/ProductCard';
import DisclaimerBox from '../components/DisclaimerBox';
import { FAQS } from '../mock/mock';
import { getProducts, getPromotions, getSettings, getReviews } from '../lib/api';
import { ALP_HERO } from '../lib/assets';
import IncomingSection from '../components/IncomingSection';
import { Stars } from '../components/Stars';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { motion, AnimatePresence } from 'framer-motion';

const ReviewStrip = ({ reviews }) => {
  const [i, setI] = useState(0);
  useEffect(() => {
    if (reviews.length < 2) return undefined;
    const t = setInterval(() => setI((x) => (x + 1) % reviews.length), 5000);
    return () => clearInterval(t);
  }, [reviews.length]);
  if (!reviews.length) return null;
  const r = reviews[i % reviews.length];
  return (
    <section className="border-b border-alp-line bg-alp-gold/[0.045]" data-testid="home-review-strip">
      <div className="max-w-5xl mx-auto px-5 md:px-8 py-4 min-h-[72px] sm:min-h-[56px] flex items-center justify-center">
        <AnimatePresence mode="wait">
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.45 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-1.5 sm:gap-4 text-center"
            data-testid="home-review-strip-item"
          >
            <Stars value={r.rating} size={13} />
            <p className="text-alp-cream/90 text-sm italic leading-snug">&ldquo;{r.body}&rdquo;</p>
            <p className="text-alp-gold text-xs whitespace-nowrap">&mdash; {r.name}{r.productName ? <span className="text-alp-gray"> &middot; {r.productName}</span> : null}</p>
          </motion.div>
        </AnimatePresence>
      </div>
      {reviews.length > 1 && (
        <div className="flex justify-center gap-1.5 pb-3 -mt-1">
          {reviews.map((_, d) => (
            <button key={d} onClick={() => setI(d)} aria-label={`Review ${d + 1}`} data-testid={`review-strip-dot-${d}`}
              className={`w-1.5 h-1.5 rounded-full transition-colors ${d === i ? 'bg-alp-gold' : 'bg-alp-line hover:bg-alp-gold/50'}`} />
          ))}
        </div>
      )}
    </section>
  );
};

const TRUST = [
  { icon: ShieldCheck, label: 'Australia Only' },
  { icon: CalendarClock, label: 'All Items In Stock' },
  { icon: Truck, label: 'Express Shipping' },
  { icon: FileCheck2, label: 'COAs Available' },
  { icon: FlaskConical, label: 'Research Only' },
];

const STEPS = [
  { icon: ClipboardList, title: 'Select & Order', text: 'Browse our in-stock research supplies. Add to cart and checkout securely in AUD.' },
  { icon: Banknote, title: 'Secure Payment', text: 'Pay by card, Afterpay or bank transfer in AUD — no surcharges. Your order is confirmed once payment is received.' },
  { icon: PackageCheck, title: 'Dispatch', text: 'Everything is held in stock and dispatches promptly after payment confirmation.' },
];

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [promos, setPromos] = useState([]);
  const [heroImage, setHeroImage] = useState(ALP_HERO);
  const [reviews, setReviews] = useState([]);
  const [stripReviews, setStripReviews] = useState([]);

  useEffect(() => {
    Promise.all([getProducts(), getReviews()]).then(([all, revs]) => {
      const feats = all.filter((p) => p.featured);
      // New Arrivals lead the featured grid.
      feats.sort((a, b) => ((b.ribbon || '').toLowerCase().includes('new') ? 1 : 0) - ((a.ribbon || '').toLowerCase().includes('new') ? 1 : 0));
      setFeatured(feats.slice(0, 4));
      const feat = (revs || []).filter((r) => r.featured);
      setReviews((feat.length ? feat : (revs || [])).slice(0, 3));
      // Strip: only reviews for IN-STOCK products, best sellers (featured products) first.
      const inStock = all.filter((p) => p.stockStatus === 'In Stock' && !p.digital && p.name);
      const matchProduct = (r) => inStock.find((p) => (r.productName || '').toLowerCase().includes(p.name.toLowerCase().trim()));
      const matched = (revs || [])
        .filter((r) => r.active !== false && matchProduct(r))
        .sort((a, b) => (matchProduct(b)?.featured === true) - (matchProduct(a)?.featured === true) || b.rating - a.rating);
      setStripReviews(matched.slice(0, 6));
    }).catch(() => {});
    getPromotions().then(setPromos).catch(() => {});
    getSettings().then((s) => { if (s?.heroImage) setHeroImage(s.heroImage); }).catch(() => {});
  }, []);

  return (
    <div className="bg-alp-black">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-alp-line">
        {/* Full-bleed hero image (background on mobile, right-side on desktop) */}
        <div className="absolute inset-0 md:left-auto md:right-0 md:w-3/5">
          <img src={heroImage} alt="Australian Luxe Peptides research vials" className="w-full h-full object-cover object-center md:object-right" />
        </div>
        {/* Mobile overlay: darken whole image so text stays readable */}
        <div className="absolute inset-0 md:hidden" style={{ background: 'linear-gradient(180deg, rgba(10,10,10,0.78) 0%, rgba(10,10,10,0.62) 55%, rgba(10,10,10,0.8) 100%)' }} />
        {/* Desktop overlay: fade from solid black on the left to clear on the right */}
        <div className="absolute inset-0 hidden md:block" style={{ background: 'linear-gradient(90deg, #0A0A0A 0%, rgba(10,10,10,0.8) 32%, rgba(10,10,10,0.15) 100%)' }} />
        <div className="absolute inset-0 hero-glow" />
        <div aria-hidden className="gold-orb w-72 h-72 -left-16 top-8 hidden md:block" />
        <div aria-hidden className="gold-orb gold-orb-slow w-96 h-96 left-[34%] -bottom-24 hidden md:block" />
        <div className="relative max-w-7xl mx-auto px-5 md:px-8 py-24 md:py-36">
          <div className="max-w-2xl fade-up">
            <p className="text-alp-gold text-[10px] sm:text-xs tracking-[0.22em] sm:tracking-[0.4em] uppercase mb-8 whitespace-nowrap">
              Australian Owned &middot; Research Grade
            </p>
            <h1 className="font-serif text-5xl md:text-7xl leading-[1.02] text-alp-cream tracking-tight tagline-glow">
              Science.<br />Purity.<br /><span className="text-gold-gradient gold-gradient-glow">Elevated.</span>
            </h1>
            <div className="w-20 h-px alp-beam my-8" />
            <p className="text-alp-cream/85 text-lg leading-relaxed max-w-md">
              Quality-tested research supplies. 98%+ purity peptides, express dispatch &mdash; exclusively and proudly Australian. Individual batch testing is carried out as we go.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 mt-10">
              <Link to="/shop" data-testid="hero-shop-instock-btn" className="btn-glow cta-shimmer inline-flex items-center justify-center gap-2 bg-alp-gold text-alp-black px-8 py-4 text-xs tracking-[0.2em] uppercase font-semibold hover:bg-alp-goldlight transition-colors">
                Shop The Range <ArrowRight size={15} />
              </Link>
              <Link to="/library" data-testid="hero-library-btn" className="btn-glow inline-flex items-center justify-center gap-2 border border-alp-gold/50 text-alp-cream px-8 py-4 text-xs tracking-[0.2em] uppercase hover:border-alp-gold hover:text-alp-gold transition-colors">
                The Library
              </Link>
            </div>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-10">
              {['98%+ Purity', 'Individually Batch Tested', 'HPLC Tested', 'Express Dispatch'].map((b) => (
                <span key={b} className="flex items-center gap-2 text-[10px] tracking-[0.22em] uppercase text-alp-cream/60">
                  <span className="w-1 h-1 rounded-full bg-alp-gold inline-block" /> {b}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* TRUST TICKER */}
      <section className="border-b border-alp-gold/15 glass-ribbon">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-7 grid grid-cols-2 md:grid-cols-5 gap-6">
          {TRUST.map((t, i) => (
            <div key={t.label} className={`flex items-center gap-3 justify-center md:justify-start reveal reveal-${Math.min(i + 1, 5)}`}>
              <t.icon size={20} strokeWidth={1.4} className="text-alp-gold shrink-0" />
              <span className="text-[11px] md:text-xs tracking-[0.18em] uppercase text-alp-cream/80">{t.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ROTATING REVIEW STRIP (in-stock products only) */}
      <ReviewStrip reviews={stripReviews} />

      {/* GIFT CARD SPOTLIGHT */}
      <section className="border-b border-alp-line relative overflow-hidden" data-testid="home-giftcard-spotlight">
        <div aria-hidden className="gold-orb w-64 h-64 -right-20 -top-20 hidden md:block" />
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-14">
          <div className="border border-alp-gold/40 rounded-xl bg-alp-gold/[0.06] px-7 py-9 md:px-12 md:py-10 flex flex-col md:flex-row items-start md:items-center gap-7 md:gap-10 card-modern">
            <div className="w-16 h-16 rounded-full border border-alp-gold/50 bg-alp-black flex items-center justify-center shrink-0">
              <Gift size={26} strokeWidth={1.4} className="text-alp-gold" />
            </div>
            <div className="flex-1">
              <p className="text-alp-gold text-xs tracking-[0.3em] uppercase mb-2">New · Digital Gift Cards</p>
              <h2 className="font-serif text-3xl md:text-4xl text-alp-cream heading-glow">Give the Gift of Luxe</h2>
              <p className="text-alp-gray text-sm md:text-base leading-relaxed mt-2 max-w-xl">
                Emailed instantly with a personal message, redeemable on anything in the boutique — and it never expires. From $25 to $1,000.
              </p>
            </div>
            <Link to="/gift-cards" data-testid="home-giftcard-cta"
              className="btn-glow cta-shimmer inline-flex items-center justify-center gap-2 bg-alp-gold text-alp-black px-8 py-4 text-xs tracking-[0.2em] uppercase font-semibold hover:bg-alp-goldlight transition-colors shrink-0">
              Shop Gift Cards <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* PROMOTIONS */}
      {promos.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 md:px-8 py-20 border-b border-alp-line" data-testid="home-promotions">
          <div className="text-center mb-12">
            <p className="text-alp-gold text-xs tracking-[0.3em] uppercase mb-3">Current Offers</p>
            <h2 className="font-serif text-4xl md:text-5xl text-alp-cream heading-glow">Promotions</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {promos.map((p) => (
              <div key={p.id} data-testid={`promo-card-${p.id}`} className="border border-alp-gold/30 rounded-lg overflow-hidden bg-alp-card/40 flex flex-col card-modern">
                {p.image ? (
                  <div className="aspect-[16/9] overflow-hidden bg-alp-dark">
                    <img src={p.image} alt={p.name} className="w-full h-full object-cover" />
                  </div>
                ) : null}
                <div className="p-6 flex flex-col flex-1">
                  {p.type && p.type !== 'General' ? <span className="text-[10px] tracking-[0.2em] uppercase text-alp-gold mb-2">{p.type}</span> : null}
                  <h3 className="font-serif text-2xl text-alp-cream mb-2">{p.name}</h3>
                  {p.promoText ? <p className="text-alp-gray text-sm leading-relaxed flex-1">{p.promoText}</p> : <p className="text-alp-gray text-sm leading-relaxed flex-1">{p.description}</p>}
                  {p.buttonLink ? (
                    <a href={p.buttonLink} className="inline-flex items-center gap-2 mt-5 text-xs tracking-[0.2em] uppercase font-semibold text-alp-gold hover:text-alp-goldlight transition-colors">
                      {p.buttonText || 'Learn More'} <ArrowRight size={14} />
                    </a>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* FEATURED */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-24">
        <div className="flex items-end justify-between mb-12">
          <div>
            <p className="text-alp-gold text-xs tracking-[0.3em] uppercase mb-3">The Collection</p>
            <h2 className="font-serif text-4xl md:text-5xl text-alp-cream heading-glow">Featured Research Supplies</h2>
          </div>
          <Link to="/shop" className="hidden md:inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-alp-cream/70 hover:text-alp-gold transition-colors">
            View All <ArrowRight size={14} />
          </Link>
        </div>
        <div className="flex sm:grid sm:grid-cols-2 lg:grid-cols-4 gap-6 overflow-x-auto sm:overflow-visible snap-x snap-mandatory -mx-5 px-5 sm:mx-0 sm:px-0 pb-4 sm:pb-0 alp-hscroll">
          {featured.map((p) => (
            <div key={p.id} className="snap-start shrink-0 w-[72%] sm:w-auto">
              <ProductCard product={p} />
            </div>
          ))}
        </div>
        <div className="text-center mt-12 md:hidden">
          <Link to="/shop" className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-alp-gold">
            View All Products <ArrowRight size={14} />
          </Link>
        </div>
      </section>

      {/* HOW ORDERING WORKS */}
      <section className="border-y border-alp-line bg-alp-dark/40 aurora-wash">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-24">
          <div className="text-center mb-14">
            <p className="text-alp-gold text-xs tracking-[0.3em] uppercase mb-3">The Process</p>
            <h2 className="font-serif text-4xl md:text-5xl text-alp-cream heading-glow">How Ordering Works</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {STEPS.map((s, i) => (
              <div key={s.title} className="text-center border border-alp-line/60 rounded-lg bg-alp-card/30 p-8 card-modern">
                <div className="mx-auto w-16 h-16 rounded-full hairline flex items-center justify-center mb-6 bg-alp-black/40">
                  <s.icon size={26} strokeWidth={1.4} className="text-alp-gold" />
                </div>
                <div className="text-alp-gold/60 font-serif text-2xl mb-2">0{i + 1}</div>
                <h3 className="font-serif text-2xl text-alp-cream mb-3">{s.title}</h3>
                <p className="text-alp-gray text-sm leading-relaxed max-w-xs mx-auto">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHATSAPP COMMUNITY */}
      <section className="border-y border-alp-gold/15 aurora-wash">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-16 md:py-20">
          <div className="glass-card rounded-lg p-8 md:p-12 flex flex-col md:flex-row items-center gap-8 md:gap-12" data-testid="community-section">
            <img
              src="https://customer-assets-jai6qajn.emergentagent.net/job_alp-luxury-store/artifacts/mvyog4aq_6A1AF3DC-3CE6-4EE2-9184-1557EE4CF620.png"
              alt="ALP Community"
              className="w-36 h-36 md:w-44 md:h-44 rounded-full object-cover shrink-0 shadow-[0_0_35px_rgba(212,175,55,0.3)]"
            />
            <div className="text-center md:text-left flex-1">
              <div className="text-alp-gold text-[10px] tracking-[0.3em] uppercase mb-3">Brand New</div>
              <h2 className="font-serif text-3xl md:text-4xl text-alp-cream heading-glow mb-3">Join Our Community</h2>
              <p className="text-alp-gray leading-relaxed max-w-xl mb-6">
                Our brand new, safe and private WhatsApp community &mdash; restock alerts, member-only offers, research talk and direct access to the ALP team, in a space we keep respectful and secure.
              </p>
              <a
                href="https://chat.whatsapp.com/CZSeIvAtdy8H7hCLTWHdHI"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="join-community-btn"
                className="btn-glow inline-flex items-center justify-center gap-2 bg-alp-gold text-alp-black px-8 py-4 text-xs tracking-[0.2em] uppercase font-semibold hover:bg-alp-goldlight transition-colors"
              >
                <MessageCircle size={15} /> Join the Community
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* PAYMENT & CHECKOUT */}
      <section className="max-w-3xl mx-auto px-5 md:px-8 py-24">
        <div className="glass-card rounded-lg p-10">
          <CreditCard size={28} strokeWidth={1.4} className="text-alp-gold mb-5" />
          <h3 className="font-serif text-3xl text-alp-cream mb-4">Payment &amp; Checkout</h3>
          <p className="text-alp-gray leading-relaxed mb-3">
            Checkout in AUD and pay by <span className="text-alp-cream">card</span>, <span className="text-alp-cream">Afterpay</span> or <span className="text-alp-cream">bank transfer</span> &mdash; no surcharges, we cover the processing fees. Your order is confirmed once payment is received.
          </p>
          <p className="text-alp-gray leading-relaxed">
            Express Post $13 &mdash; free on in-stock orders over $300. Australia only.
          </p>
        </div>
      </section>

      {/* FAQ PREVIEW */}
      <section className="border-t border-alp-line bg-alp-dark/40">
        <div className="max-w-3xl mx-auto px-5 md:px-8 py-24">
          <div className="text-center mb-12">
            <p className="text-alp-gold text-xs tracking-[0.3em] uppercase mb-3">Questions</p>
            <h2 className="font-serif text-4xl md:text-5xl text-alp-cream heading-glow">Frequently Asked</h2>
          </div>
          <Accordion type="single" collapsible className="w-full">
            {FAQS.slice(0, 5).map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border-alp-line">
                <AccordionTrigger className="text-alp-cream hover:text-alp-gold text-left font-serif text-xl">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-alp-gray leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <div className="text-center mt-10">
            <Link to="/faq" className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-alp-gold hover:text-alp-goldlight transition-colors">
              View All FAQs <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 md:px-8 py-16">
        <IncomingSection />
      </section>

      {reviews.length > 0 && (
        <section className="border-t border-alp-line bg-alp-dark/40" data-testid="home-reviews">
          <div className="max-w-7xl mx-auto px-5 md:px-8 py-20">
            <div className="flex items-end justify-between mb-10">
              <div>
                <p className="text-alp-gold text-xs tracking-[0.3em] uppercase mb-2">Client Feedback</p>
                <h2 className="font-serif text-3xl md:text-4xl text-alp-cream">What Our Clients Say</h2>
              </div>
              <Link to="/reviews" className="text-xs tracking-[0.18em] uppercase text-alp-gold hover:text-alp-goldlight transition-colors shrink-0">View all &rarr;</Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {reviews.map((r) => (
                <article key={r.id} className="border border-alp-line rounded-lg bg-alp-card/40 p-6 card-modern">
                  <Stars value={r.rating} />
                  {r.body ? <p className="text-alp-cream/85 text-sm leading-relaxed mt-4 line-clamp-5">{r.body}</p> : null}
                  <p className="text-alp-cream text-sm font-medium mt-5">{r.name}</p>
                  {r.productName ? <p className="text-alp-gray text-xs">{r.productName}</p> : null}
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="max-w-7xl mx-auto px-5 md:px-8 py-16">
        <DisclaimerBox />
      </section>
    </div>
  );
}
